<template>
  <div class="flex flex-wrap items-center gap-2">
    <ui-popover :open="showDatePicker" @update:open="(value) => showDatePicker = value">
      <ui-popover-trigger as-child>
        <button
          type="button"
          data-slot="date-time-input-date"
          :disabled="disabled"
          class="glass-input flex h-9 w-36 items-center gap-2 rounded-md px-3 text-sm tabular-nums focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          @focus="() => $emit('focus')"
        >
          <calendar-icon class="size-4 shrink-0 text-muted-foreground" />
          {{ formattedDate }}
        </button>
      </ui-popover-trigger>
      <ui-popover-content align="start" class="w-auto p-0">
        <ui-date-calendar
          :model-value="internalDate"
          :min-date="minDate"
          :locale="$root.language"
          @update:model-value="(date) => onDateSelected(date)"
        />
      </ui-popover-content>
    </ui-popover>

    <div class="flex items-center gap-1">
      <number-input
        ref="hourInput"
        v-model="hourInput"
        class="w-14 text-center"
        :aria-label="tr('Hour')"
        :min="0"
        :max="23"
        :disabled="disabled"
        @focus="(e) => onFocusInput(e)"
        @blur="() => onBlurInput()"
      />
      <span class="text-muted-foreground">:</span>
      <number-input
        v-model="minuteInput"
        class="w-14 text-center"
        :aria-label="tr('Minute')"
        :min="0"
        :max="59"
        :disabled="disabled"
        @focus="(e) => onFocusInput(e)"
        @blur="() => onBlurInput()"
      />
    </div>
  </div>
</template>

<script>
import { Calendar } from '@lucide/vue';

import NumberInput from '@/ui/components/NumberInput.vue';

/** Date (calendar popover) + hour and minute fields bound to a Date. */
export default {
  name: 'DateTimeInput',
  components: {
    CalendarIcon: Calendar,
    NumberInput,
  },
  props: {
    modelValue: {
      type: Date,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    minDate: {
      type: Date,
      required: false,
      default: () => new Date(0),
    },
  },
  emits: [ 'update:modelValue', 'focus' ],
  data() {
    return {
      formattedDate: null,
      internalDate: this.date(this.modelValue),
      hourInput: null,
      minuteInput: null,
      showDatePicker: false,
    };
  },
  watch: {
    modelValue(newVal) {
      this.internalDate = this.date(newVal);
    },
    hourInput(val, oldVal) {
      if (oldVal === null) {
        return;
      }

      val = parseInt(val);
      oldVal = parseInt(oldVal);

      if (isNaN(val)) {
        val = 0;
      }

      if (val !== oldVal) {
        this.internalDate.setHours(val);
        this.updateValue();
      }
    },
    minuteInput(val, oldVal) {
      if (oldVal === null) {
        return;
      }

      val = parseInt(val);
      oldVal = parseInt(oldVal);

      if (isNaN(val)) {
        val = 0;
      }

      if (val !== oldVal) {
        this.internalDate.setMinutes(val);
        this.updateValue();
      }
    },
  },
  created() {
    this.setDateInputValue();
    this.paddingTimeInputs();
  },
  methods: {
    date(originalDate) {
      const now = new Date(originalDate ?? 'now');
      now.setSeconds(0);
      now.setMilliseconds(0);

      return now;
    },
    paddingTimeInputs() {
      this.hourInput = ('0' + this.internalDate.getHours()).slice(-2);
      this.minuteInput = ('0' + this.internalDate.getMinutes()).slice(-2);
    },
    // Local date, like the hour and minute fields (toISOString would be UTC).
    setDateInputValue() {
      const date = this.internalDate;
      this.formattedDate = [ date.getFullYear(), date.getMonth() + 1, date.getDate() ]
        .map(part => String(part).padStart(2, '0'))
        .join('-');
    },
    onDateSelected(date) {
      // One call: setting the month first could overflow (e.g. the 31st into a 30-day month).
      this.internalDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
      this.updateValue();
      this.setDateInputValue();
      this.closeDatePicker();
    },
    closeDatePicker() {
      this.showDatePicker = false;
      this.$refs.hourInput.focus();
    },
    updateValue() {
      const newValue = this.date(this.internalDate);

      this.$emit('update:modelValue', newValue);
    },
    onFocusInput(e) {
      e.target.select();
      this.$emit('focus');
    },
    onBlurInput() {
      this.paddingTimeInputs();
    },
  },
};
</script>
